import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const mcpSalesCallsArticle: BlogArticle = {
  key: BlogArticleKey.MCP_SALES_CALLS,
  slug: BlogSlugs.MCP_SALES_CALLS,
  categoryId: BlogCategory.AI_TOOLS,
  date: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Connecter Claude ou ChatGPT à vos appels de vente avec le MCP",
      excerpt:
        "Coller des transcriptions dans un chat fonctionne pour un appel, pas pour un portefeuille de clients. Le MCP donne à Claude ou ChatGPT un accès en lecture à vos échanges, avec des réponses sourcées.",
      description:
        "Ce que le MCP change pour une équipe commerciale : un assistant qui cherche dans vos appels, compte les thèmes et cite ses sources, en lecture seule.",
      ctaTitle: "Interrogez vos conversations clients depuis votre assistant",
      ctaText:
        "On transcrit et on classe vos échanges clients, puis on les ouvre en lecture à Claude ou ChatGPT, avec des réponses qui citent les passages. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Vous avez sans doute déjà collé une transcription d'appel dans ChatGPT ou Claude pour en tirer un résumé ou préparer une relance. Cela fonctionne, pour un appel. Au dixième, l'exercice montre ses limites : la conversation déborde, l'assistant a oublié le premier appel, et des propos confidentiels de vos clients circulent dans des copier coller.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le Model Context Protocol, ou MCP, propose une autre approche. Au lieu d'apporter vos appels à l'assistant, vous donnez à l'assistant un accès en lecture à l'endroit où ils sont rangés. Voici ce que cela change pour une équipe commerciale, ce qu'on peut lui demander, et les précautions à prendre avant de le brancher.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Le MCP, en termes simples",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le Model Context Protocol est un standard ouvert présenté par Anthropic en novembre 2024. Il décrit une façon commune, pour un assistant d'IA, d'appeler des outils et de lire des données situées dans d'autres applications. Une application qui l'adopte expose un serveur MCP : une liste d'outils, chacun avec un nom, une description de ce qu'il fait et les paramètres qu'il accepte.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quand vous posez une question, l'assistant choisit lui même les outils utiles, les appelle, lit ce qu'ils renvoient et rédige sa réponse à partir de ce qu'il a récupéré. Claude, ChatGPT, Claude Code ou Cursor savent se connecter à un serveur MCP, ce qui évite de construire une intégration différente pour chacun d'eux.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Coller une transcription, c'est prêter un document à l'assistant. Le connecter en MCP, c'est lui ouvrir la bibliothèque, en lecture.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Pourquoi coller des transcriptions ne tient pas",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le copier coller rend service ponctuellement. Il se heurte à quatre limites dès qu'on veut raisonner sur un portefeuille de clients plutôt que sur un appel isolé.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**La fenêtre de contexte.** Un modèle ne lit qu'un volume de texte limité à la fois. Une heure d'appel passe, vingt heures d'échanges avec un même compte beaucoup moins bien, et l'ensemble de vos clients jamais.",
            "**Aucune mémoire d'une conversation à l'autre.** Chaque nouvelle discussion repart de zéro. Pour comparer deux comptes ou suivre un deal sur trois mois, il faut tout recoller, à chaque fois.",
            "**Des données confidentielles qui circulent.** Les transcriptions contiennent des noms, des budgets, des tensions internes. Les copier à la main dans des conversations personnelles multiplie les copies, sans trace de qui a partagé quoi.",
            "**Aucune citation vérifiable.** L'assistant résume ce que vous lui avez donné, mais rien ne relie sa phrase au passage exact de l'appel. Pour vérifier, il faut relire la transcription entière.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "S'y ajoute un biais plus discret : on colle les appels dont on se souvient. L'analyse porte donc sur une sélection faite de mémoire, rarement sur l'ensemble de ce que les clients ont dit.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que change un connecteur vers vos conversations clients",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avec un connecteur, l'assistant ne reçoit plus un texte, il reçoit des outils. Relié à une base de conversations clients déjà transcrites et analysées, il peut notamment :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**chercher dans vos échanges** avec les mots qu'un client aurait employés, en filtrant par client, par période, par type d'échange ou par étape de deal ;",
            "**lire une fiche client** : la synthèse du compte, ses deals, les engagements encore ouverts et les derniers échanges ;",
            "**compter des thèmes** : regrouper les passages d'une même catégorie, les objections par exemple, et dire combien de fois chaque sujet revient au lieu de l'estimer ;",
            "**citer des passages** : appuyer chaque affirmation sur un extrait, en précisant qui l'a dit et dans quel échange, et lire les passages voisins pour replacer une phrase dans son contexte.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence tient en une phrase : l'assistant ne travaille plus sur ce que vous avez pensé à lui donner, mais sur tout ce que l'équipe a capturé, et il va chercher lui même ce dont il a besoin pour répondre.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Coller des transcriptions dans un chat", "Connecter un serveur MCP"],
          rows: [
            [
              "Un ou quelques appels, dans la limite du contexte",
              "Tous les échanges de l'espace de travail, lus à la demande",
            ],
            [
              "Tout est à recoller dans chaque nouvelle conversation",
              "Les échanges restent à leur place, l'assistant y retourne",
            ],
            [
              "Des copies manuelles de données sensibles",
              "Un accès en lecture, accordé explicitement et révocable",
            ],
            ["Un résumé sans source", "Des réponses appuyées sur des passages cités"],
            ["Des fréquences estimées au jugé", "Des thèmes comptés sur les passages classés"],
            ["Les appels dont on se souvient", "L'ensemble de ce qui a été enregistré"],
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Dix questions à poser à votre assistant",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Voici des demandes concrètes qu'un responsable commercial peut formuler une fois le connecteur en place. Les noms et les situations sont des exemples ; la qualité de chaque réponse dépend de ce que vos échanges contiennent réellement.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Préparer et suivre un compte",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "« Je vois le client Axelia jeudi. Résume la relation, les engagements encore ouverts de notre côté et les questions restées sans réponse au dernier appel. »",
            "« Qu'est ce que le client a promis de faire depuis notre dernière démo, et qu'est ce que nous lui avons promis ? »",
            "« Récapitule ce compte en dix lignes pour la personne qui reprend le dossier, avec une citation pour chaque point important. »",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Comprendre ce qui revient",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "« Quelles objections reviennent le plus dans nos démos depuis trois mois ? Donne le nombre de passages pour chacune. »",
            "« Quels concurrents sont cités pendant les négociations, et à quel propos ? »",
            "« Que disent les clients quand ils abordent le budget ? Cite trois passages représentatifs. »",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Comparer et apprendre",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "« Compare ce que disaient les clients sur les deals gagnés et sur les deals perdus : quels arguments reviennent d'un côté et pas de l'autre ? »",
            "« Pour le persona des directeurs financiers, quels thèmes reviennent le plus souvent ? »",
            "« Quels clients ont exprimé un risque de départ ces dernières semaines, et avec quels mots ? »",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Préparer une action",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "« Rédige un brouillon de relance pour ce client en reprenant la phrase exacte où il décrit son problème. Je le relirai avant de l'envoyer. »",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce dernier exemple appelle une précision. Un message rédigé par l'assistant reste un brouillon : une personne le relit, vérifie le passage qu'il reprend, l'ajuste et l'envoie sous son nom. Le connecteur lit, il n'envoie rien.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Sécurité et gouvernance : la liste à vérifier",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ouvrir des conversations clients à un assistant d'IA n'est pas anodin. Avant de brancher un connecteur, quel que soit l'outil, vérifiez les points suivants.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Lecture seule.** Le connecteur doit pouvoir lire sans pouvoir modifier, supprimer ni déclencher d'action payante.",
            "**Un périmètre limité.** L'assistant ne doit voir que les espaces de travail auxquels l'utilisateur connecté appartient déjà.",
            "**Un consentement explicite.** La connexion passe par un écran d'autorisation qui indique à quel client l'accès est accordé, jamais par un mot de passe partagé.",
            "**Une révocation simple.** Vous devez pouvoir couper l'accès d'un assistant à tout moment, et voir lesquels sont réellement connectés.",
            "**Qui voit quoi.** Chaque personne qui connecte son assistant n'accède qu'à ce qu'elle voit déjà dans l'application. Décidez qui, dans l'équipe, a besoin de cet usage.",
            "**Le fournisseur du modèle.** Les passages lus par l'assistant sont transmis au fournisseur de ce modèle. Vérifiez ses conditions et les réglages de votre compte, notamment sur l'usage des données pour l'entraînement.",
            "**Le RGPD et l'information des personnes enregistrées.** Un appel enregistré contient des données personnelles. Les participants doivent être informés de l'enregistrement et de son usage, et la finalité doit rester la connaissance des clients, pas la notation des personnes.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un bon connecteur ne crée aucun droit nouveau : il ouvre à l'assistant ce que l'utilisateur voit déjà, ni plus, ni moins.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Connecter Claude ou ChatGPT à Meidly",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Chez nous, le connecteur fait partie du produit. Vos appels, enregistrés sur Google Meet avec l'extension Chrome ou importés sous forme de fichier audio, de fichier vidéo ou de lien vers un enregistrement, sont transcrits, rattachés à une fiche client et découpés en passages classés. C'est cette base que le connecteur ouvre à votre assistant.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Trois étapes",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Ouvrez la page **MCP, API & Intégrations** de votre espace.",
            "Pour Claude ou ChatGPT, ajoutez le connecteur depuis votre assistant, puis autorisez l'accès en un clic sur l'écran de consentement. Aucun jeton à copier.",
            "Pour les autres clients MCP, comme Claude Code ou Cursor, générez un jeton depuis la même page et ajoutez le à la configuration du client.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le connecteur est en lecture seule : il n'écrit rien dans vos données et ne consomme aucun crédit. Il donne accès aux espaces de travail, aux clients, aux échanges et à leurs passages, aux thèmes comptés et aux personas. La page indique quels assistants sont effectivement connectés, et vous pouvez en déconnecter un à tout moment.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Connecter votre assistant à Meidly",
          text: "Depuis la page MCP, API & Intégrations, autorisez Claude ou ChatGPT en un clic, ou générez un jeton pour un autre client. Lecture seule, aucun crédit consommé.",
          linkLabel: "Ouvrir les intégrations",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.H2,
          text: "Les limites, honnêtement",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un connecteur ne rend pas l'assistant infaillible. Trois limites restent à garder en tête.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**La réponse ne vaut que ce qui a été capturé.** Un appel non enregistré ou une décision prise dans un couloir n'existent pas pour l'assistant. S'il ne trouve rien, cela ne prouve pas que rien n'a été dit.",
            "**Un modèle peut mal lire.** Il peut sortir une phrase de son contexte, confondre deux interlocuteurs ou généraliser à partir de deux passages. Une transcription peut aussi contenir des erreurs de reconnaissance.",
            "**La citation est là pour être vérifiée.** Avant de reprendre une affirmation dans une décision ou dans un message, ouvrez le passage cité et lisez ce qui l'entoure.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Utilisé ainsi, l'assistant ne remplace pas la relecture, il la rend possible : au lieu de réécouter vingt appels, vous vérifiez les trois passages sur lesquels repose sa réponse.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Par où commencer",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Rassemblez les enregistrements de quelques comptes actifs, avec l'accord des participants.",
            "Connectez un seul assistant, pour une ou deux personnes de l'équipe, et regardez ce qu'il voit.",
            "Posez d'abord des questions dont vous connaissez la réponse, pour juger la qualité des citations.",
            "Élargissez ensuite aux questions de fréquence et de comparaison, là où le gain est le plus net.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le bénéfice ne se voit pas sur la première question. Il apparaît le jour où vous demandez ce qui revient dans cinquante appels et obtenez une réponse comptée, avec les passages pour la vérifier.",
        },
      ],
      faq: [
        {
          question: "Qu'est ce que le Model Context Protocol ?",
          answer:
            "Un standard ouvert présenté par Anthropic en novembre 2024. Il permet à un assistant d'IA comme Claude ou ChatGPT d'appeler des outils et de lire des données situées dans d'autres applications, sans intégration spécifique pour chaque assistant.",
        },
        {
          question: "L'assistant peut-il modifier les données de l'espace de travail ?",
          answer:
            "Non. Notre connecteur est en lecture seule : il lit les clients, les échanges, les passages, les thèmes et les personas, mais il n'écrit rien, ne supprime rien et ne déclenche aucune action payante.",
        },
        {
          question: "Quelle différence avec coller une transcription dans ChatGPT ?",
          answer:
            "Une transcription collée est limitée par le contexte du modèle, oubliée à la conversation suivante et résumée sans source. Avec le connecteur, l'assistant cherche lui même dans tous les échanges auxquels vous avez accès et cite les passages sur lesquels il s'appuie.",
        },
        {
          question: "Le connecteur consomme-t-il des crédits ?",
          answer:
            "Non, les lectures par le connecteur ne consomment aucun crédit. Dans l'application, l'analyse d'un échange coûte un crédit par tranche entamée de 1 000 caractères de sa transcription, et une question posée à l'assistant intégré coûte un crédit.",
        },
        {
          question: "Quelles précautions prendre au regard du RGPD ?",
          answer:
            "Informer les participants de l'enregistrement et de son usage, limiter l'accès aux personnes qui en ont besoin, vérifier les conditions du fournisseur du modèle connecté, et garder une finalité de connaissance des clients, jamais de notation des personnes.",
        },
      ],
    },
    en: {
      title: "Connecting Claude or ChatGPT to your sales calls with MCP",
      excerpt:
        "Pasting transcripts into a chat works for one call, not for a book of accounts. MCP gives Claude or ChatGPT read access to your customer conversations, with sourced answers.",
      description:
        "What MCP changes for a sales team: an assistant that searches your calls, counts recurring themes and quotes its sources, with read only access.",
      ctaTitle: "Query your customer conversations from your assistant",
      ctaText:
        "We transcribe and tag your customer exchanges, then open them read only to Claude or ChatGPT, with answers that quote the passages. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You have probably pasted a call transcript into ChatGPT or Claude to get a summary or draft a follow up. It works, for one call. By the tenth, the approach shows its limits: the conversation overflows, the assistant has forgotten the first call, and confidential words from your customers are travelling through copy and paste.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The Model Context Protocol, or MCP, takes another route. Instead of bringing your calls to the assistant, you give the assistant read access to the place where they are stored. Here is what that changes for a sales team, what you can ask, and the precautions to take before plugging it in.",
        },
        {
          type: ArticleBlockType.H2,
          text: "MCP in plain words",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The Model Context Protocol is an open standard introduced by Anthropic in November 2024. It describes a common way for an AI assistant to call tools and read data held in other applications. An application that adopts it exposes an MCP server: a list of tools, each with a name, a description of what it does and the parameters it accepts.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "When you ask a question, the assistant picks the relevant tools itself, calls them, reads what they return and writes its answer from what it retrieved. Claude, ChatGPT, Claude Code and Cursor can all connect to an MCP server, which spares you from building a different integration for each of them.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Pasting a transcript lends the assistant a document. Connecting it through MCP opens the library to it, read only.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Why pasting transcripts does not scale",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Copy and paste helps now and then. It runs into four limits as soon as you want to reason about a book of accounts rather than a single call.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**The context window.** A model only reads a limited amount of text at once. One hour of call fits, twenty hours of exchanges with one account far less well, and all of your customers never.",
            "**No memory from one conversation to the next.** Every new chat starts from zero. To compare two accounts or follow a deal over three months, you paste everything again, every time.",
            "**Confidential data on the move.** Transcripts hold names, budgets, internal tensions. Copying them by hand into personal chats multiplies the copies, with no trace of who shared what.",
            "**No verifiable citation.** The assistant summarises what you gave it, but nothing ties its sentence to the exact passage of the call. To check, you have to reread the whole transcript.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Add a quieter bias: you paste the calls you remember. The analysis therefore covers a selection made from memory, rarely everything your customers actually said.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What a connector to your customer conversations changes",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "With a connector, the assistant no longer receives a text, it receives tools. Linked to a base of customer conversations already transcribed and analysed, it can for instance:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**search your exchanges** using the words a customer would have used, filtered by customer, period, exchange type or deal stage;",
            "**read a customer file**: the account synthesis, its deals, the commitments still open and the latest exchanges;",
            "**count themes**: group the passages of one category, objections for example, and say how often each subject comes back instead of guessing;",
            "**quote passages**: back every claim with an excerpt, saying who said it and in which exchange, and read the neighbouring passages to put a sentence back in context.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference fits in one sentence: the assistant no longer works on what you thought of giving it, but on everything the team captured, and it fetches what it needs to answer on its own.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Pasting transcripts into a chat", "Connecting an MCP server"],
          rows: [
            [
              "One or a few calls, within the context limit",
              "Every exchange of the workspace, read on demand",
            ],
            [
              "Everything has to be pasted again in each new chat",
              "The exchanges stay where they are, the assistant comes back to them",
            ],
            ["Manual copies of sensitive data", "Read access, explicitly granted and revocable"],
            ["A summary with no source", "Answers backed by quoted passages"],
            ["Frequencies guessed by feel", "Themes counted from tagged passages"],
            ["The calls you happen to remember", "Everything that was recorded"],
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Ten questions to ask your assistant",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Here are concrete requests a sales leader can make once the connector is in place. Names and situations are examples; the quality of each answer depends on what your exchanges actually contain.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Prepare and follow an account",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            '"I am meeting Axelia on Thursday. Summarise the relationship, the commitments still open on our side and the questions left unanswered on the last call."',
            '"What has the customer promised to do since our last demo, and what did we promise them?"',
            '"Recap this account in ten lines for the person taking it over, with a quote for each important point."',
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Understand what keeps coming back",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            '"Which objections come up most in our demos over the last three months? Give the number of passages for each."',
            '"Which competitors are mentioned during negotiations, and about what?"',
            '"What do customers say when they talk about budget? Quote three representative passages."',
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Compare and learn",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            '"Compare what customers said on won deals and on lost deals: which arguments come up on one side and not the other?"',
            '"For the finance director persona, which themes come back most often?"',
            '"Which customers expressed a churn risk in recent weeks, and in what words?"',
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Prepare an action",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            '"Draft a follow up for this customer using the exact sentence where they describe their problem. I will review it before sending."',
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That last example needs a word of caution. A message written by the assistant stays a draft: a person reviews it, checks the passage it quotes, adjusts it and sends it under their own name. The connector reads, it sends nothing.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Security and governance: the checklist",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Opening customer conversations to an AI assistant is not a small step. Before plugging in a connector, whatever the tool, check the following points.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Read only.** The connector must be able to read without being able to modify, delete or trigger any paid action.",
            "**A limited scope.** The assistant must only see the workspaces the connected user already belongs to.",
            "**Explicit consent.** The connection goes through an authorisation screen showing which client is being granted access, never through a shared password.",
            "**Easy revocation.** You must be able to cut an assistant's access at any time, and see which ones are really connected.",
            "**Who sees what.** Each person who connects their assistant only reaches what they already see in the application. Decide who in the team needs this use.",
            "**The model provider.** The passages the assistant reads are sent to the provider of that model. Check its terms and your account settings, in particular on whether data is used for training.",
            "**GDPR and informing the people recorded.** A recorded call holds personal data. Participants must be informed of the recording and its use, and the purpose must remain knowing your customers, not rating individuals.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A good connector creates no new right: it opens to the assistant what the user already sees, no more, no less.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Connecting Claude or ChatGPT to Meidly",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "With us, the connector is part of the product. Your calls, recorded on Google Meet with the Chrome extension or imported as an audio file, a video file or a link to a recording, are transcribed, attached to a customer file and cut into tagged passages. That base is what the connector opens to your assistant.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Three steps",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Open the **MCP, API & Integrations** page of your account.",
            "For Claude or ChatGPT, add the connector from your assistant, then authorise access in one click on the consent screen. No token to copy.",
            "For other MCP clients, such as Claude Code or Cursor, generate a token from the same page and add it to the client's configuration.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The connector is read only: it writes nothing to your data and consumes no credit. It gives access to the workspaces, the customers, the exchanges and their passages, the counted themes and the personas. The page shows which assistants are actually connected, and you can disconnect any of them at any time.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Connect your assistant to Meidly",
          text: "From the MCP, API & Integrations page, authorise Claude or ChatGPT in one click, or generate a token for another client. Read only, no credit consumed.",
          linkLabel: "Open integrations",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.H2,
          text: "The limits, honestly",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A connector does not make the assistant infallible. Three limits are worth keeping in mind.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**The answer is only as good as what was captured.** A call that was not recorded or a decision made in a corridor does not exist for the assistant. If it finds nothing, that does not prove nothing was said.",
            "**A model can misread.** It can take a sentence out of context, mix up two speakers or generalise from two passages. A transcript can also contain recognition errors.",
            "**The citation is there to be checked.** Before reusing a claim in a decision or a message, open the quoted passage and read what surrounds it.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Used this way, the assistant does not replace review, it makes review possible: instead of listening to twenty calls again, you check the three passages its answer rests on.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Where to start",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Gather the recordings of a few active accounts, with the participants' consent.",
            "Connect a single assistant, for one or two people in the team, and look at what it sees.",
            "Start with questions you already know the answer to, to judge the quality of the citations.",
            "Then widen to questions of frequency and comparison, where the gain is clearest.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The benefit does not show on the first question. It shows the day you ask what keeps coming back across fifty calls and get a counted answer, with the passages to check it.",
        },
      ],
      faq: [
        {
          question: "What is the Model Context Protocol?",
          answer:
            "An open standard introduced by Anthropic in November 2024. It lets an AI assistant such as Claude or ChatGPT call tools and read data held in other applications, without a specific integration for each assistant.",
        },
        {
          question: "Can the assistant modify the workspace's data?",
          answer:
            "No. Our connector is read only: it reads customers, exchanges, passages, themes and personas, but it writes nothing, deletes nothing and triggers no paid action.",
        },
        {
          question: "How is this different from pasting a transcript into ChatGPT?",
          answer:
            "A pasted transcript is capped by the model's context, forgotten in the next chat and summarised without a source. With the connector, the assistant searches every exchange you have access to on its own and quotes the passages it relies on.",
        },
        {
          question: "Does the connector consume credits?",
          answer:
            "No, reads through the connector consume no credit. In the app, analysing an exchange costs one credit per started block of 1,000 characters of its transcript, and a question asked to the built in assistant costs one credit.",
        },
        {
          question: "What GDPR precautions should you take?",
          answer:
            "Inform participants of the recording and its use, limit access to the people who need it, check the terms of the connected model's provider, and keep the purpose to knowing your customers, never to rating individuals.",
        },
      ],
    },
  },
};
